import { useState, useRef, useCallback } from "react";
import { MapContainer, TileLayer, Marker, Popup, Polyline, LayersControl, useMap } from "react-leaflet";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import html2canvas from "html2canvas";
import jsPDF from "jspdf";

delete (L.Icon.Default.prototype as any)._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: "https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon-2x.png",
  iconUrl: "https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon.png",
  shadowUrl: "https://unpkg.com/leaflet@1.9.4/dist/images/marker-shadow.png",
});

// ─── Interfaces ───────────────────────────────────────────────────────────────

interface GenericItem {
  acontecimento: string;
  idade: number;
  ref: string;
}

interface GenealogiaItem {
  nome: string;
  ref: string;
  paternidade: number;
  morte: number;
}

interface MapPoint {
  nome: string;
  lat: number;
  lng: number;
  evento: string;
  ref: string;
  ordem: number;
}

interface MapPersonagem {
  label: string;
  cor: string;
  pontos: MapPoint[];
}

// ─── Timeline Data ─────────────────────────────────────────────────────────────

const abraaoData: GenericItem[] = [
  { acontecimento: "Abrão parte de Harã e entra em Canaã", idade: 75, ref: "12.4-5" },
  { acontecimento: "Nasce Ismael, filho de Abrão com Agar", idade: 85, ref: "16.3-4" },
  { acontecimento: "Nasce Isaque, filho de Abraão com Sara", idade: 100, ref: "21.5" },
  { acontecimento: "Morre Sara, esposa de Abraão", idade: 137, ref: "23.1" },
  { acontecimento: "Isaque casa-se com Rebeca", idade: 140, ref: "25.20" },
  { acontecimento: "Morre Abraão", idade: 175, ref: "25.7" },
];

const isaqueData: GenericItem[] = [
  { acontecimento: "Nascimento de Isaque, filho da promessa", idade: 0, ref: "21.1-7" },
  { acontecimento: "Isaque oferecido por Abraão no monte Moriá", idade: 20, ref: "22.2-14" },
  { acontecimento: "Morte de Sara, mãe de Isaque", idade: 37, ref: "23.1-2" },
  { acontecimento: "Isaque casa-se com Rebeca", idade: 40, ref: "25.20" },
  { acontecimento: "Nascem Esaú e Jacó", idade: 60, ref: "25.26" },
  { acontecimento: "Morte de Abraão, pai de Isaque", idade: 75, ref: "25.7-8" },
  { acontecimento: "Isaque vai a Gerar; aliança com Abimeleque; escava poços", idade: 100, ref: "26.1-33" },
  { acontecimento: "Isaque abençoa Jacó em vez de Esaú (enganado)", idade: 137, ref: "27.1-40" },
  { acontecimento: "Isaque envia Jacó a Harã para buscar esposa", idade: 137, ref: "28.1-5" },
  { acontecimento: "Morte de Isaque em Hebrom", idade: 180, ref: "35.28-29" },
];

const jacoData: GenericItem[] = [
  { acontecimento: "Morte de Abraão, avô de Jacó", idade: 15, ref: "25.7" },
  { acontecimento: "Jacó é enviado a Labão, em Harã", idade: 77, ref: "28.5" },
  { acontecimento: "Jacó casa-se com Lia e Raquel", idade: 84, ref: "29.21-30; 30.1,22-26" },
  { acontecimento: "Nasce José, filho de Jacó e Raquel", idade: 91, ref: "30.22-24" },
  { acontecimento: "Jacó foge de Labão para Canaã", idade: 97, ref: "cap. 31" },
  { acontecimento: "José, filho de Jacó, é vendido como escravo", idade: 108, ref: "37.12-36" },
  { acontecimento: "Morte de Isaque, pai de Jacó", idade: 120, ref: "35.28-29; cf. 25.26 com 35.28" },
  { acontecimento: "Jacó reencontra José e muda-se com sua família para o Egito", idade: 130, ref: "caps. 46-47; cf. 47.9,28" },
  { acontecimento: "Morte de Jacó", idade: 147, ref: "47.28" },
];

const joseData: GenericItem[] = [
  { acontecimento: "Jacó, pai de José, muda-se com sua família de Harã para Canaã", idade: 6, ref: "31.17-21" },
  { acontecimento: "José é vendido para Potifar no Egito", idade: 17, ref: "cap. 37" },
  { acontecimento: "José interpreta os sonhos do copeiro e do padeiro na prisão", idade: 28, ref: "cap. 40" },
  { acontecimento: "Morte de Isaque, avô de José", idade: 29, ref: "35.28-29" },
  { acontecimento: "José interpreta os sonhos do Faraó e é liberto da prisão", idade: 30, ref: "41.1-36" },
  { acontecimento: "Sete anos de fartura; Manassés e Efraim nascem nesse período", idade: 30, ref: "41.47-52" },
  { acontecimento: "Sete anos de fome; José se reencontra com seus irmãos e pai", idade: 37, ref: "41.53-47.26" },
  { acontecimento: "Morte de Jacó, pai de José", idade: 56, ref: "47.28" },
  { acontecimento: "Morte de José", idade: 110, ref: "50.22-26" },
];

const genealogiaData: GenealogiaItem[] = [
  { nome: "Adão", ref: "5.3-5", paternidade: 130, morte: 930 },
  { nome: "Sete", ref: "5.6-8", paternidade: 105, morte: 912 },
  { nome: "Enoque", ref: "5.9-11", paternidade: 90, morte: 905 },
  { nome: "Cainã", ref: "5.12-14", paternidade: 70, morte: 910 },
  { nome: "Maalalel", ref: "5.15-17", paternidade: 65, morte: 895 },
  { nome: "Jarede", ref: "5.18-20", paternidade: 162, morte: 962 },
  { nome: "Enoque", ref: "5.21-24", paternidade: 65, morte: 365 },
  { nome: "Metusalém", ref: "5.25-27", paternidade: 187, morte: 969 },
  { nome: "Lameque", ref: "5.28-31", paternidade: 182, morte: 777 },
  { nome: "Noé", ref: "5.32; 9.29", paternidade: 500, morte: 950 },
  { nome: "Sem", ref: "11.10-11", paternidade: 100, morte: 600 },
  { nome: "Arfaxade", ref: "11.12-13", paternidade: 35, morte: 438 },
  { nome: "Salá", ref: "11.14-15", paternidade: 30, morte: 433 },
  { nome: "Héber", ref: "11.16-17", paternidade: 34, morte: 464 },
  { nome: "Pelegue", ref: "11.18-19", paternidade: 30, morte: 239 },
  { nome: "Reú", ref: "11.20-21", paternidade: 32, morte: 239 },
  { nome: "Serugue", ref: "11.22-23", paternidade: 30, morte: 230 },
  { nome: "Naor", ref: "11.24-25", paternidade: 29, morte: 148 },
  { nome: "Tera", ref: "11.26-32", paternidade: 70, morte: 205 },
  { nome: "Abraão", ref: "21.5; 25.7", paternidade: 100, morte: 175 },
  { nome: "Isaque", ref: "25.26; 35.28", paternidade: 60, morte: 180 },
  { nome: "Jacó", ref: "30.22-24; 47.28", paternidade: 91, morte: 147 },
];

// ─── Map Data ──────────────────────────────────────────────────────────────────

const mapPersonagens: MapPersonagem[] = [
  {
    label: "Abraão", cor: "#8b1a1a",
    pontos: [
      { ordem: 1, nome: "Ur dos Caldeus", lat: 30.9626, lng: 46.1033, evento: "Origem de Abrão e sua família", ref: "Gn 11.28-31" },
      { ordem: 2, nome: "Harã", lat: 36.8631, lng: 39.0244, evento: "Parada com Terá; morte de Terá; chamado de Abrão aos 75 anos", ref: "Gn 11.31–12.4" },
      { ordem: 3, nome: "Siquém", lat: 32.2095, lng: 35.2868, evento: "Primeira parada em Canaã; Deus aparece e promete a terra", ref: "Gn 12.6-7" },
      { ordem: 4, nome: "Betel", lat: 31.9251, lng: 35.2219, evento: "Abrão constrói altar ao Senhor", ref: "Gn 12.8" },
      { ordem: 5, nome: "Egito", lat: 30.0444, lng: 31.2357, evento: "Fuga da fome; episódio de Sara no Egito", ref: "Gn 12.10-20" },
      { ordem: 6, nome: "Betel (retorno)", lat: 31.9351, lng: 35.23, evento: "Retorno ao altar; separação de Ló", ref: "Gn 13.1-12" },
      { ordem: 7, nome: "Hebrom / Manre", lat: 31.5326, lng: 35.0998, evento: "Residência principal; visita dos três anjos; promessa de Isaque", ref: "Gn 13.18; 18.1" },
      { ordem: 8, nome: "Dã", lat: 33.2488, lng: 35.6517, evento: "Resgate de Ló — perseguição até Dã", ref: "Gn 14.14" },
      { ordem: 9, nome: "Hobá (Damasco)", lat: 33.5138, lng: 36.2765, evento: "Derrota dos reis inimigos até Hobá", ref: "Gn 14.15" },
      { ordem: 10, nome: "Berseba", lat: 31.2516, lng: 34.7913, evento: "Aliança com Abimeleque; Abraão planta tamargueira", ref: "Gn 21.31-33" },
      { ordem: 11, nome: "Monte Moriá", lat: 31.7767, lng: 35.2353, evento: "Oferta de Isaque; anjo do Senhor interrompe", ref: "Gn 22.2-14" },
    ],
  },
  {
    label: "Isaque", cor: "#7a4e1a",
    pontos: [
      { ordem: 1, nome: "Berseba", lat: 31.243, lng: 34.798, evento: "Local de nascimento e residência de Abraão; Isaque cresce aqui", ref: "Gn 21.31; 22.19" },
      { ordem: 2, nome: "Monte Moriá", lat: 31.7767, lng: 35.2353, evento: "Isaque oferecido por Abraão; resgatado pelo anjo do Senhor", ref: "Gn 22.2-14" },
      { ordem: 3, nome: "Hebrom", lat: 31.5326, lng: 35.0998, evento: "Morte e sepultamento de Sara; Isaque encontra Rebeca aqui", ref: "Gn 23.2; 24.67" },
      { ordem: 4, nome: "Laai-Roi", lat: 30.85, lng: 34.65, evento: "Isaque medita no campo ao entardecer; encontra Rebeca vinda de Harã", ref: "Gn 24.62-67" },
      { ordem: 5, nome: "Gerar", lat: 31.3197, lng: 34.5483, evento: "Isaque vai a Gerar na fome; episódio com Abimeleque e Rebeca", ref: "Gn 26.1-11" },
      { ordem: 6, nome: "Vale de Gerar (poços)", lat: 31.28, lng: 34.56, evento: "Isaque escava os poços Esec, Sitna e Reobote", ref: "Gn 26.12-22" },
      { ordem: 7, nome: "Berseba (retorno)", lat: 31.252, lng: 34.793, evento: "Deus aparece a Isaque; aliança com Abimeleque; poço Seba", ref: "Gn 26.23-33" },
      { ordem: 8, nome: "Hebrom (morte)", lat: 31.522, lng: 35.108, evento: "Morte de Isaque aos 180 anos; sepultado por Esaú e Jacó", ref: "Gn 35.27-29" },
    ],
  },
  {
    label: "Jacó", cor: "#1a5c8b",
    pontos: [
      { ordem: 1, nome: "Berseba", lat: 31.241, lng: 34.80, evento: "Jacó parte de Berseba em direção a Harã", ref: "Gn 28.10" },
      { ordem: 2, nome: "Betel (Luz)", lat: 31.915, lng: 35.21, evento: "Sonho da escada; Deus renova a promessa abraâmica", ref: "Gn 28.11-19" },
      { ordem: 3, nome: "Harã", lat: 36.873, lng: 39.034, evento: "Jacó serve Labão 20 anos; casa-se com Lia e Raquel; filhos nascem", ref: "Gn 29–31" },
      { ordem: 4, nome: "Gileade", lat: 32.0, lng: 35.75, evento: "Aliança de Jacó com Labão no monte Gileade", ref: "Gn 31.44-55" },
      { ordem: 5, nome: "Peniel", lat: 32.0179, lng: 35.5457, evento: "Luta com o anjo; Jacó recebe o nome Israel", ref: "Gn 32.22-32" },
      { ordem: 6, nome: "Siquém", lat: 32.22, lng: 35.295, evento: "Jacó compra um campo; episódio de Diná", ref: "Gn 33.18-19; cap. 34" },
      { ordem: 7, nome: "Betel (retorno)", lat: 31.925, lng: 35.225, evento: "Deus aparece novamente; confirmação do nome Israel", ref: "Gn 35.9-15" },
      { ordem: 8, nome: "Belém (Efrata)", lat: 31.7054, lng: 35.2024, evento: "Morte de Raquel ao dar à luz Benjamim", ref: "Gn 35.16-20" },
      { ordem: 9, nome: "Hebrom", lat: 31.522, lng: 35.108, evento: "Jacó retorna a Hebrom; morte de Isaque", ref: "Gn 35.27-29" },
      { ordem: 10, nome: "Gósen (Egito)", lat: 30.6, lng: 31.85, evento: "Jacó desce ao Egito; reencontro com José; morre aos 147 anos", ref: "Gn 46–47; 49.33" },
    ],
  },
  {
    label: "José", cor: "#1a7a3c",
    pontos: [
      { ordem: 1, nome: "Hebrom", lat: 31.512, lng: 35.118, evento: "José enviado por Jacó para encontrar seus irmãos", ref: "Gn 37.13-14" },
      { ordem: 2, nome: "Dotã", lat: 32.4167, lng: 35.2, evento: "Irmãos jogam José na cisterna; vendido a mercadores ismaelitas", ref: "Gn 37.17-28" },
      { ordem: 3, nome: "Casa de Potifar (Egito)", lat: 30.055, lng: 31.225, evento: "José vendido a Potifar; acusado e preso", ref: "Gn 39" },
      { ordem: 4, nome: "Prisão no Egito", lat: 30.045, lng: 31.235, evento: "José interpreta sonhos do copeiro e do padeiro", ref: "Gn 40" },
      { ordem: 5, nome: "Palácio do Faraó", lat: 30.065, lng: 31.245, evento: "Interpreta sonhos do Faraó; nomeado governador do Egito", ref: "Gn 41.14-44" },
      { ordem: 6, nome: "Gósen (Egito)", lat: 30.61, lng: 31.86, evento: "José instala sua família na terra de Gósen", ref: "Gn 46.28-34; 47.11" },
    ],
  },
];

// ─── Constants ─────────────────────────────────────────────────────────────────

const MAIN_TABS = ["Abraão", "Isaque", "Jacó", "José", "Detalhes"];
const DETAIL_SUBTABS = ["Genealogia", "Mapa"];
const maxGenealogia = Math.max(...genealogiaData.map((d) => d.morte));

const tabColors: Record<string, string> = {
  Abraão: "#8b1a1a",
  Isaque: "#7a4e1a",
  Jacó: "#1a5c8b",
  José: "#1a7a3c",
};

// ─── PDF Export hook ───────────────────────────────────────────────────────────

function usePdfExport(contentRef: React.RefObject<HTMLDivElement | null>, filename: string) {
  const [exporting, setExporting] = useState(false);

  const exportPdf = useCallback(async () => {
    if (!contentRef.current) return;
    setExporting(true);
    try {
      const canvas = await html2canvas(contentRef.current, {
        scale: 2,
        useCORS: true,
        backgroundColor: "#f7f0e0",
        logging: false,
      } as any);
      const imgData = canvas.toDataURL("image/png");
      const pdf = new jsPDF({ orientation: "portrait", unit: "mm", format: "a4" });
      const pageW = pdf.internal.pageSize.getWidth();
      const pageH = pdf.internal.pageSize.getHeight();
      const margin = 10;
      const usableW = pageW - margin * 2;
      const imgH = (canvas.height * usableW) / canvas.width;
      let y = margin;
      let remaining = imgH;

      // Multi-page support
      while (remaining > 0) {
        const sliceH = Math.min(remaining, pageH - margin * 2);
        const srcY = (imgH - remaining) * (canvas.height / imgH);
        const srcH = sliceH * (canvas.height / imgH);
        const sliceCanvas = document.createElement("canvas");
        sliceCanvas.width = canvas.width;
        sliceCanvas.height = srcH;
        const ctx = sliceCanvas.getContext("2d")!;
        ctx.drawImage(canvas, 0, srcY, canvas.width, srcH, 0, 0, canvas.width, srcH);
        const sliceData = sliceCanvas.toDataURL("image/png");
        if (y > margin) pdf.addPage();
        pdf.addImage(sliceData, "PNG", margin, margin, usableW, sliceH);
        remaining -= sliceH;
        y += sliceH;
      }

      pdf.save(`${filename}.pdf`);
    } finally {
      setExporting(false);
    }
  }, [contentRef, filename]);

  return { exportPdf, exporting };
}

// ─── PDF Button ────────────────────────────────────────────────────────────────

interface PdfButtonProps {
  contentRef: React.RefObject<HTMLDivElement | null>;
  filename: string;
}

function PdfButton({ contentRef, filename }: PdfButtonProps) {
  const { exportPdf, exporting } = usePdfExport(contentRef, filename);
  return (
    <button
      onClick={exportPdf}
      disabled={exporting}
      title="Exportar como PDF"
      style={{
        display: "flex", alignItems: "center", gap: 6,
        padding: "8px 16px", borderRadius: 20,
        border: "1px solid #c8a96e",
        background: exporting ? "#e8dcc8" : "#3d2b1f",
        color: exporting ? "#9a8060" : "#c8a96e",
        fontSize: 12, cursor: exporting ? "not-allowed" : "pointer",
        fontFamily: "Georgia, serif", transition: "all 0.2s",
        boxShadow: "0 2px 8px rgba(0,0,0,0.15)",
      }}>
      {exporting ? (
        <>
          <span style={{ display: "inline-block", width: 12, height: 12, border: "2px solid #c8a96e", borderTopColor: "transparent", borderRadius: "50%", animation: "spin 0.8s linear infinite" }} />
          Gerando...
        </>
      ) : (
        <>
          <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
            <polyline points="7 10 12 15 17 10" />
            <line x1="12" y1="15" x2="12" y2="3" />
          </svg>
          Exportar PDF
        </>
      )}
    </button>
  );
}

// ─── Helpers ───────────────────────────────────────────────────────────────────

function makeIcon(cor: string, ordem: number, highlighted: boolean) {
  const size = highlighted ? 36 : 28;
  const border = highlighted ? "3px solid #fff" : "2px solid #fff";
  const shadow = highlighted
    ? "0 0 0 3px " + cor + ", 0 4px 12px rgba(0,0,0,0.5)"
    : "0 2px 6px rgba(0,0,0,0.4)";
  return L.divIcon({
    className: "",
    html: `<div style="background:${cor};color:#fff;width:${size}px;height:${size}px;border-radius:50%;display:flex;align-items:center;justify-content:center;font-size:${highlighted ? 14 : 12}px;font-weight:bold;font-family:Georgia,serif;border:${border};box-shadow:${shadow};transition:all 0.2s;">${ordem}</div>`,
    iconSize: [size, size],
    iconAnchor: [size / 2, size / 2],
    popupAnchor: [0, -(size / 2 + 4)],
  });
}

function FlyTo({ lat, lng }: { lat: number; lng: number }) {
  const map = useMap();
  map.flyTo([lat, lng], 7, { duration: 1.2 });
  return null;
}

// ─── TimelineBar ──────────────────────────────────────────────────────────────

interface TimelineBarProps { value: number; max: number; color: string; label: string; }
function TimelineBar({ value, max, color, label }: TimelineBarProps) {
  const pct = (value / max) * 100;
  return (
    <div style={{ display: "flex", alignItems: "center", gap: 6, width: "100%" }}>
      <div style={{ flex: 1, background: "#e8dcc8", borderRadius: 3, height: 10, overflow: "hidden" }}>
        <div style={{ width: `${pct}%`, height: "100%", background: color, borderRadius: 3, transition: "width 0.6s ease" }} />
      </div>
      <span style={{ fontSize: 11, color: "#6b5a3e", minWidth: 28, textAlign: "right", fontFamily: "serif" }}>{label}</span>
    </div>
  );
}

// ─── TabShell (wraps each tab content + PDF button) ───────────────────────────

interface TabShellProps {
  title: string;
  subtitle: string;
  cor: string;
  filename: string;
  children: React.ReactNode;
}

function TabShell({ title, subtitle, cor, filename, children }: TabShellProps) {
  const ref = useRef<HTMLDivElement | null>(null);
  return (
    <div>
      {/* Header row */}
      <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", marginBottom: 4, gap: 12, flexWrap: "wrap" }}>
        <div>
          <h2 style={{ color: cor, fontWeight: "normal", fontSize: 20, margin: 0 }}>{title}</h2>
          <p style={{ color: "#9a8060", fontSize: 12, margin: "4px 0 16px", fontStyle: "italic" }}>{subtitle}</p>
        </div>
        <PdfButton contentRef={ref} filename={filename} />
      </div>
      {/* Captured area */}
      <div ref={ref}>
        {/* PDF title watermark inside captured area */}
        <div style={{
          display: "none",
        }} className="pdf-header">
          <p style={{ color: cor, fontFamily: "Georgia, serif", fontSize: 16, fontWeight: "bold", margin: "0 0 2px" }}>{title}</p>
          <p style={{ color: "#9a8060", fontFamily: "Georgia, serif", fontSize: 11, fontStyle: "italic", margin: "0 0 16px" }}>{subtitle} · Cronologia Bíblica do Gênesis</p>
        </div>
        {children}
      </div>
    </div>
  );
}

// ─── GenericTimelineTab ───────────────────────────────────────────────────────

interface GenericTimelineTabProps { data: GenericItem[]; cor: string; }
function GenericTimelineTab({ data, cor }: GenericTimelineTabProps) {
  const [selected, setSelected] = useState<number | null>(null);
  const idades = data.map((d) => d.idade);
  const minI = Math.min(...idades);
  const maxI = Math.max(...idades);
  return (
    <div>
      <p style={{ color: "#7a6040", fontStyle: "italic", fontSize: 13, marginBottom: 20, fontFamily: "Georgia, serif" }}>
        Clique em um evento para destacá-lo.
      </p>
      <div style={{ position: "relative", marginBottom: 32 }}>
        <div style={{ height: 4, background: "#c8a96e", borderRadius: 2, position: "relative" }}>
          {data.map((d, i) => {
            const pct = maxI === minI ? 50 : ((d.idade - minI) / (maxI - minI)) * 100;
            const isSel = selected === i;
            return (
              <div key={i} onClick={() => setSelected(selected === i ? null : i)}
                style={{
                  position: "absolute", left: `${pct}%`, top: "50%", transform: "translate(-50%,-50%)",
                  width: isSel ? 18 : 12, height: isSel ? 18 : 12, borderRadius: "50%",
                  background: isSel ? cor : "#c8a96e",
                  border: `2px solid ${isSel ? cor : "#8b6914"}`,
                  cursor: "pointer", transition: "all 0.2s", zIndex: 2,
                  boxShadow: isSel ? `0 0 0 4px ${cor}44` : "none",
                }} />
            );
          })}
        </div>
        <div style={{ display: "flex", justifyContent: "space-between", marginTop: 10 }}>
          <span style={{ fontSize: 11, color: "#9a8060", fontFamily: "Georgia, serif" }}>{minI} anos</span>
          <span style={{ fontSize: 11, color: "#9a8060", fontFamily: "Georgia, serif" }}>{maxI} anos</span>
        </div>
      </div>
      <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
        {data.map((d, i) => {
          const isSel = selected === i;
          return (
            <div key={i} onClick={() => setSelected(selected === i ? null : i)}
              style={{
                background: isSel ? "#f5ead0" : "#faf6ee",
                border: `1px solid ${isSel ? cor : "#e8dcc8"}`,
                borderLeft: `4px solid ${isSel ? cor : "#c8a96e"}`,
                borderRadius: 6, padding: "12px 16px", cursor: "pointer",
                transition: "all 0.2s", display: "flex", alignItems: "center", gap: 16,
              }}>
              <div style={{
                minWidth: 48, height: 48, borderRadius: "50%",
                background: isSel ? cor : "#c8a96e",
                display: "flex", alignItems: "center", justifyContent: "center",
                flexDirection: "column", transition: "background 0.2s",
              }}>
                <span style={{ color: "#fff", fontSize: 16, fontWeight: "bold", fontFamily: "Georgia, serif", lineHeight: 1 }}>{d.idade}</span>
                <span style={{ color: "rgba(255,255,255,0.8)", fontSize: 9 }}>anos</span>
              </div>
              <div style={{ flex: 1 }}>
                <p style={{ margin: 0, fontFamily: "Georgia, serif", fontSize: 14, color: "#3d2b1f", lineHeight: 1.4 }}>{d.acontecimento}</p>
                <p style={{ margin: "4px 0 0", fontSize: 11, color: "#9a8060", fontFamily: "monospace" }}>Gênesis {d.ref}</p>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ─── GenealogiasTab ───────────────────────────────────────────────────────────

function GenealogiasTab() {
  const [sortBy, setSortBy] = useState<string>("ordem");
  const [hoveredIdx, setHoveredIdx] = useState<number | null>(null);
  const sorted = [...genealogiaData].sort((a, b) => {
    if (sortBy === "morte") return b.morte - a.morte;
    if (sortBy === "paternidade") return b.paternidade - a.paternidade;
    return 0;
  });
  return (
    <div>
      <div style={{ display: "flex", gap: 8, marginBottom: 20, flexWrap: "wrap" }}>
        {([["ordem", "Ordem bíblica"], ["morte", "Mais longevos"], ["paternidade", "Idade na paternidade"]] as [string, string][]).map(([v, l]) => (
          <button key={v} onClick={() => setSortBy(v)}
            style={{
              padding: "6px 14px", borderRadius: 20, border: "1px solid #c8a96e",
              background: sortBy === v ? "#8b1a1a" : "#faf6ee",
              color: sortBy === v ? "#fff" : "#7a6040",
              fontSize: 12, cursor: "pointer", fontFamily: "Georgia, serif", transition: "all 0.2s",
            }}>{l}</button>
        ))}
      </div>
      <div style={{ display: "flex", gap: 20, marginBottom: 16 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
          <div style={{ width: 24, height: 8, background: "#c8a96e", borderRadius: 2 }} />
          <span style={{ fontSize: 11, color: "#7a6040", fontFamily: "Georgia, serif" }}>Idade ao ter filho</span>
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
          <div style={{ width: 24, height: 8, background: "#4a4a4a", borderRadius: 2 }} />
          <span style={{ fontSize: 11, color: "#7a6040", fontFamily: "Georgia, serif" }}>Idade na morte</span>
        </div>
      </div>
      <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
        {sorted.map((d, i) => {
          const isHovered = hoveredIdx === i;
          return (
            <div key={d.nome + d.ref}
              onMouseEnter={() => setHoveredIdx(i)}
              onMouseLeave={() => setHoveredIdx(null)}
              style={{
                background: isHovered ? "#f5ead0" : "#faf6ee",
                border: `1px solid ${isHovered ? "#c8a96e" : "#e8dcc8"}`,
                borderRadius: 6, padding: "10px 14px", transition: "all 0.15s",
              }}>
              <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 6 }}>
                <span style={{ fontFamily: "Georgia, serif", fontSize: 14, fontWeight: "bold", color: "#3d2b1f", minWidth: 90 }}>{d.nome}</span>
                <span style={{ fontSize: 10, color: "#9a8060", fontFamily: "monospace" }}>Gn {d.ref}</span>
                {isHovered && (
                  <span style={{ marginLeft: "auto", fontSize: 11, color: "#8b1a1a", fontFamily: "Georgia, serif", fontStyle: "italic" }}>
                    {d.paternidade} → {d.morte} anos
                  </span>
                )}
              </div>
              <TimelineBar value={d.paternidade} max={maxGenealogia} color="#c8a96e" label={`${d.paternidade}`} />
              <div style={{ marginTop: 4 }}>
                <TimelineBar value={d.morte} max={maxGenealogia} color="#4a4a4a" label={`${d.morte}`} />
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ─── Map PDF export (map screenshot + text list) ──────────────────────────────

async function exportMapPdf(ativos: Record<string, boolean>, mapElRef: React.RefObject<HTMLDivElement | null>) {
  const pdf = new jsPDF({ orientation: "portrait", unit: "mm", format: "a4" });
  const pageW = pdf.internal.pageSize.getWidth();
  const margin = 14;
  const usableW = pageW - margin * 2;
  let y = margin;

  // Title
  pdf.setFontSize(16); pdf.setFont("helvetica", "bold"); pdf.setTextColor(61, 43, 31);
  pdf.text("Mapa das Jornadas dos Patriarcas", margin, y); y += 7;
  pdf.setFontSize(9); pdf.setFont("helvetica", "italic"); pdf.setTextColor(154, 128, 96);
  pdf.text("Abraão, Isaque, Jacó e José — Oriente Médio Antigo", margin, y); y += 5;
  pdf.setDrawColor(200, 169, 110); pdf.setLineWidth(0.5);
  pdf.line(margin, y, margin + usableW, y); y += 5;

  // Map screenshot
  if (mapElRef.current) {
    try {
      const mapCanvas = await html2canvas(mapElRef.current, {
        useCORS: true, allowTaint: true,
        backgroundColor: "#d4e8f0", logging: false,
      } as any);
      const imgData = mapCanvas.toDataURL("image/png");
      const imgH = Math.min((mapCanvas.height * usableW) / mapCanvas.width, 95);
      pdf.addImage(imgData, "PNG", margin, y, usableW, imgH);
      y += imgH + 6;
    } catch { y += 2; }
  }

  // Points list
  pdf.setDrawColor(200, 169, 110); pdf.setLineWidth(0.3);
  pdf.line(margin, y, margin + usableW, y); y += 5;

  mapPersonagens.forEach((p) => {
    if (!ativos[p.label]) return;
    const rgb = hexToRgb(p.cor);
    if (y > 265) { pdf.addPage(); y = margin; }

    pdf.setFontSize(11); pdf.setFont("helvetica", "bold"); pdf.setTextColor(...rgb);
    pdf.text(`${p.label}  —  ${p.pontos.length} locais`, margin, y); y += 6;

    p.pontos.forEach((pt) => {
      if (y > 272) { pdf.addPage(); y = margin; }
      pdf.setFillColor(...rgb);
      pdf.circle(margin + 3, y - 2.5, 3, "F");
      pdf.setFontSize(7); pdf.setFont("helvetica", "bold"); pdf.setTextColor(255, 255, 255);
      pdf.text(String(pt.ordem), margin + 3, y - 1.2, { align: "center" });
      pdf.setFontSize(10); pdf.setFont("helvetica", "bold"); pdf.setTextColor(61, 43, 31);
      pdf.text(pt.nome, margin + 8, y - 1); y += 4;
      pdf.setFontSize(8.5); pdf.setFont("helvetica", "normal"); pdf.setTextColor(80, 60, 40);
      const lines = pdf.splitTextToSize(pt.evento, usableW - 10);
      lines.forEach((l: string) => { if (y > 275) { pdf.addPage(); y = margin; } pdf.text(l, margin + 8, y); y += 4; });
      pdf.setFontSize(7.5); pdf.setTextColor(154, 128, 96);
      pdf.text(pt.ref, margin + 8, y); y += 5;
    });

    y += 2; pdf.setDrawColor(232, 220, 200); pdf.setLineWidth(0.2);
    pdf.line(margin, y, margin + usableW, y); y += 5;
  });

  pdf.setFontSize(7.5); pdf.setTextColor(176, 144, 96); pdf.setFont("helvetica", "italic");
  pdf.text("Dados extraídos das notas da Bíblia de Estudo NVI", margin, 290);
  pdf.save("mapa-patriarcas.pdf");
}

function hexToRgb(hex: string): [number, number, number] {
  const r = parseInt(hex.slice(1, 3), 16);
  const g = parseInt(hex.slice(3, 5), 16);
  const b = parseInt(hex.slice(5, 7), 16);
  return [r, g, b];
}

// ─── MapaTab ──────────────────────────────────────────────────────────────────

function MapaTab() {
  const [ativos, setAtivos] = useState<Record<string, boolean>>({ Abraão: true, Isaque: true, Jacó: true, José: true });
  const [highlighted, setHighlighted] = useState<{ label: string; ordem: number } | null>(null);
  const [flyTarget, setFlyTarget] = useState<{ lat: number; lng: number } | null>(null);
  const mapContainerRef = useRef<HTMLDivElement | null>(null);

  const toggle = (label: string) => {
    setAtivos((prev) => ({ ...prev, [label]: !prev[label] }));
    setHighlighted(null);
  };

  const handleLegendClick = (p: MapPersonagem, pt: MapPoint) => {
    setHighlighted({ label: p.label, ordem: pt.ordem });
    setFlyTarget({ lat: pt.lat, lng: pt.lng });
  };

  return (
    <div>
      <p style={{ color: "#7a6040", fontStyle: "italic", fontSize: 13, marginBottom: 12, fontFamily: "Georgia, serif" }}>
        Clique nos locais abaixo para destacar no mapa.
      </p>

      {/* Toggles + PDF button */}
      <div style={{ display: "flex", gap: 8, marginBottom: 16, flexWrap: "wrap", alignItems: "center" }}>
        {mapPersonagens.map((p) => (
          <button key={p.label} onClick={() => toggle(p.label)}
            style={{
              padding: "6px 16px", borderRadius: 20,
              border: `2px solid ${p.cor}`,
              background: ativos[p.label] ? p.cor : "transparent",
              color: ativos[p.label] ? "#fff" : p.cor,
              fontSize: 13, cursor: "pointer", fontFamily: "Georgia, serif",
              fontWeight: "bold", transition: "all 0.2s",
            }}>{p.label}</button>
        ))}
        <button
          onClick={() => exportMapPdf(ativos, mapContainerRef)}
          title="Exportar locais visíveis como PDF"
          style={{
            display: "flex", alignItems: "center", gap: 6,
            padding: "8px 16px", borderRadius: 20,
            border: "1px solid #c8a96e",
            background: "#3d2b1f", color: "#c8a96e",
            fontSize: 12, cursor: "pointer",
            fontFamily: "Georgia, serif", marginLeft: "auto",
            boxShadow: "0 2px 8px rgba(0,0,0,0.15)",
          }}>
          <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
            <polyline points="7 10 12 15 17 10" />
            <line x1="12" y1="15" x2="12" y2="3" />
          </svg>
          Exportar PDF
        </button>
      </div>

      <div ref={mapContainerRef} style={{ borderRadius: 10, overflow: "hidden", border: "2px solid #c8a96e", height: 500 }}>
        <MapContainer center={[32.0, 36.0]} zoom={5} style={{ height: "100%", width: "100%" }} scrollWheelZoom={false}>
          {flyTarget && <FlyTo lat={flyTarget.lat} lng={flyTarget.lng} />}
          <LayersControl position="topright">
            <LayersControl.BaseLayer checked name="Mapa Padrão">
              <TileLayer
                attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
                url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
              />
            </LayersControl.BaseLayer>
            <LayersControl.BaseLayer name="Satélite (Esri)">
              <TileLayer
                attribution="Tiles &copy; Esri"
                url="https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}"
              />
            </LayersControl.BaseLayer>
          </LayersControl>
          {mapPersonagens.map((p) =>
            ativos[p.label] ? (
              <span key={p.label}>
                <Polyline
                  positions={p.pontos.map((pt) => [pt.lat, pt.lng] as [number, number])}
                  pathOptions={{ color: p.cor, weight: 2.5, opacity: 0.7, dashArray: "6 4" }}
                />
                {p.pontos.map((pt) => {
                  const isHighlighted = highlighted?.label === p.label && highlighted?.ordem === pt.ordem;
                  return (
                    <Marker
                      key={p.label + pt.ordem}
                      position={[pt.lat, pt.lng]}
                      icon={makeIcon(p.cor, pt.ordem, isHighlighted)}
                      ref={(ref) => { if (ref && isHighlighted) ref.openPopup(); }}
                    >
                      <Popup>
                        <div style={{ fontFamily: "Georgia, serif", minWidth: 190 }}>
                          <p style={{ margin: "0 0 4px", fontWeight: "bold", fontSize: 14, color: p.cor }}>{pt.nome}</p>
                          <p style={{ margin: "0 0 4px", fontSize: 13, color: "#3d2b1f", lineHeight: 1.4 }}>{pt.evento}</p>
                          <p style={{ margin: 0, fontSize: 11, color: "#9a8060", fontFamily: "monospace" }}>{pt.ref}</p>
                        </div>
                      </Popup>
                    </Marker>
                  );
                })}
              </span>
            ) : null
          )}
        </MapContainer>
      </div>
      <div style={{ marginTop: 16, display: "flex", flexDirection: "column", gap: 12 }}>
        {mapPersonagens.map((p) =>
          ativos[p.label] ? (
            <div key={p.label}>
              <p style={{ margin: "0 0 6px", fontFamily: "Georgia, serif", fontSize: 13, fontWeight: "bold", color: p.cor }}>
                {p.label} — {p.pontos.length} locais
              </p>
              <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
                {p.pontos.map((pt) => {
                  const isHighlighted = highlighted?.label === p.label && highlighted?.ordem === pt.ordem;
                  return (
                    <span key={pt.ordem} onClick={() => handleLegendClick(p, pt)}
                      style={{
                        fontSize: 11, fontFamily: "Georgia, serif",
                        background: isHighlighted ? p.cor : "#faf6ee",
                        border: `1px solid ${p.cor}`, borderRadius: 4, padding: "3px 10px",
                        color: isHighlighted ? "#fff" : "#3d2b1f",
                        cursor: "pointer", transition: "all 0.2s",
                        boxShadow: isHighlighted ? `0 0 0 2px ${p.cor}55` : "none",
                      }}>
                      <span style={{ color: isHighlighted ? "#fff" : p.cor, fontWeight: "bold" }}>{pt.ordem}.</span> {pt.nome}
                    </span>
                  );
                })}
              </div>
            </div>
          ) : null
        )}
      </div>
    </div>
  );
}

// ─── App ───────────────────────────────────────────────────────────────────────

export default function App() {
  const [activeTab, setActiveTab] = useState<number>(0);
  const [detailSub, setDetailSub] = useState<number>(0);
  const [detailsOpen, setDetailsOpen] = useState<boolean>(false);

  const handleTabClick = (i: number) => {
    if (i === 4) { setDetailsOpen((prev) => !prev); }
    else { setActiveTab(i); setDetailsOpen(false); }
  };

  const handleSubTab = (i: number) => {
    setDetailSub(i); setActiveTab(4); setDetailsOpen(false);
  };

  return (
    <>
      {/* Spinner keyframe */}
      <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>

      <div style={{
        minHeight: "100vh",
        background: "linear-gradient(135deg, #f7f0e0 0%, #ede3cc 50%, #f7f0e0 100%)",
        fontFamily: "Georgia, serif", padding: "0 0 40px",
      }}>
        {/* Header */}
        <div style={{
          background: "linear-gradient(180deg, #3d2b1f 0%, #5a3e2b 100%)",
          padding: "32px 24px 24px", textAlign: "center",
          position: "relative", overflow: "hidden",
        }}>
          <div style={{
            position: "absolute", inset: 0, opacity: 0.07,
            backgroundImage: "repeating-linear-gradient(45deg,#c8a96e 0,#c8a96e 1px,transparent 0,transparent 50%)",
            backgroundSize: "12px 12px",
          }} />
          <p style={{ color: "#c8a96e", fontSize: 11, letterSpacing: 4, textTransform: "uppercase", margin: "0 0 8px", position: "relative" }}>
            Cronologia Bíblica
          </p>
          <h1 style={{ color: "#f7f0e0", fontSize: 26, margin: "0 0 6px", fontFamily: "Georgia, serif", fontWeight: "normal", position: "relative", letterSpacing: 1 }}>
            Linhas do Tempo do Gênesis
          </h1>
          <p style={{ color: "#c8a96e", fontSize: 12, margin: 0, fontStyle: "italic", position: "relative" }}>
            Patriarcas e Genealogias
          </p>
        </div>

        {/* Main tabs */}
        <div style={{ background: "#2e1f14", borderBottom: "2px solid #c8a96e", position: "relative" }}>
          <div style={{ display: "flex", overflowX: "auto" }}>
            {MAIN_TABS.map((tab, i) => {
              const isActive = i < 4 ? activeTab === i : activeTab === 4;
              const isDetails = i === 4;
              return (
                <button key={tab} onClick={() => handleTabClick(i)}
                  style={{
                    flex: 1, minWidth: 70, padding: "14px 8px",
                    background: isActive ? "#c8a96e" : "transparent",
                    color: isActive ? "#2e1f14" : "#c8a96e",
                    border: "none", cursor: "pointer",
                    fontFamily: "Georgia, serif", fontSize: 13,
                    fontWeight: isActive ? "bold" : "normal",
                    transition: "all 0.2s", letterSpacing: 0.5, whiteSpace: "nowrap",
                  }}>
                  {tab}{isDetails ? (detailsOpen ? " ▲" : " ▼") : ""}
                </button>
              );
            })}
          </div>
          {detailsOpen && (
            <div style={{
              position: "absolute", right: 0, top: "100%",
              background: "#3d2b1f", border: "1px solid #c8a96e",
              borderTop: "none", borderRadius: "0 0 8px 8px",
              zIndex: 100, minWidth: 160, boxShadow: "0 4px 16px rgba(0,0,0,0.3)",
            }}>
              {DETAIL_SUBTABS.map((sub, i) => (
                <button key={sub} onClick={() => handleSubTab(i)}
                  style={{
                    display: "block", width: "100%", padding: "12px 20px",
                    background: activeTab === 4 && detailSub === i ? "#c8a96e" : "transparent",
                    color: activeTab === 4 && detailSub === i ? "#2e1f14" : "#c8a96e",
                    border: "none", cursor: "pointer",
                    fontFamily: "Georgia, serif", fontSize: 13,
                    textAlign: "left", transition: "all 0.15s",
                  }}>{sub}</button>
              ))}
            </div>
          )}
        </div>

        {/* Content */}
        <div style={{ maxWidth: 720, margin: "0 auto", padding: "24px 16px" }}>

          {activeTab === 0 && (
            <TabShell title="Linha do Tempo de Abraão" subtitle="Dos 75 aos 175 anos — 100 anos de caminhada com Deus" cor={tabColors["Abraão"]} filename="cronologia-abraao">
              <GenericTimelineTab data={abraaoData} cor={tabColors["Abraão"]} />
            </TabShell>
          )}

          {activeTab === 1 && (
            <TabShell title="Linha do Tempo de Isaque" subtitle="Do nascimento aos 180 anos — filho da promessa" cor={tabColors["Isaque"]} filename="cronologia-isaque">
              <GenericTimelineTab data={isaqueData} cor={tabColors["Isaque"]} />
            </TabShell>
          )}

          {activeTab === 2 && (
            <TabShell title="Linha do Tempo de Jacó" subtitle="Dos 15 aos 147 anos" cor={tabColors["Jacó"]} filename="cronologia-jaco">
              <GenericTimelineTab data={jacoData} cor={tabColors["Jacó"]} />
            </TabShell>
          )}

          {activeTab === 3 && (
            <TabShell title="Linha do Tempo de José" subtitle="Dos 6 aos 110 anos" cor={tabColors["José"]} filename="cronologia-jose">
              <GenericTimelineTab data={joseData} cor={tabColors["José"]} />
            </TabShell>
          )}

          {activeTab === 4 && detailSub === 0 && (
            <TabShell title="Genealogias" subtitle="Idade na paternidade e idade na morte — de Adão a Jacó" cor="#3d2b1f" filename="genealogias-genesis">
              <GenealogiasTab />
            </TabShell>
          )}

          {activeTab === 4 && detailSub === 1 && (
            <div>
              <h2 style={{ color: "#3d2b1f", fontWeight: "normal", fontSize: 20, margin: "0 0 4px" }}>Mapa das Jornadas</h2>
              <p style={{ color: "#9a8060", fontSize: 12, margin: "0 0 20px", fontStyle: "italic" }}>Abraão, Isaque, Jacó e José — rotas no Oriente Médio Antigo</p>
              <MapaTab />
            </div>
          )}
        </div>

        <div style={{ textAlign: "center", marginTop: 16 }}>
          <p style={{ color: "#b09060", fontSize: 11, fontStyle: "italic" }}>Dados extraídos das notas da Bíblia de Estudo NVI</p>
        </div>
      </div>
    </>
  );
}
